import { auth } from "firebase/app";

import VueRouter, { Route, RawLocation } from "vue-router";
import { runPendingChangesGuard } from "@/utils/PendingChangesGuard";

import AllDevices from "@/components/allDevices/AllDevices.vue";
import DeviceTemplateComponent from "@/components/allDevices/DeviceTemplateComponent.vue";
import DevicesComponent from "@/components/allDevices/DevicesComponent.vue";
import AllDevicesListComponent from "@/components/allDevices/AllDevicesListComponent.vue";
import DeviceHistoryComponent from "@/components/allDevices/DeviceHistory.vue";
import IresDeviceList from "@/components/devices/IresDeviceList.vue";
import IresDeviceSampleListComponent from "@/components/devices/IresDeviceSampleList.vue";
import IresDeviceDetailComponent from "@/components/devices/IresDeviceDetail.vue";
import RateDryingComponent from "@/components/devices/ratedrying/RateDrying.vue";
import DryingHistoryComponent from "@/components/devices/DryingHistory.vue";
import LoginComponent from "@/components/login/Login.vue";
import ResetPasswordComponent from "@/components/login/ResetPassword.vue";
import Scaffold from "@/components/Scaffold.vue";
import MapsPageComponent from "@/components/MapsPage.vue";
import ProjectListComponent from "@/components/project/list/ProjectsList.vue";
import ArchivedProjectListComponent from "@/components/project/list/ArchivedProjectsList.vue";
import UserManagementComponent from "@/components/users/UserManagement.vue";
import VehicleManagementComponent from "@/components/settings/vehicle/VehicleManagement.vue";
import RoleManagementComponent from "@/components/users/RoleManagement.vue";
import ProjectSettingsComponent from "@/components/settings/project/ProjectSettings.vue";
import ProjectEditingSettingsComponent from "@/components/settings/project/ProjectEditingSettings.vue";
import ProjectDevicesSettingsComponent from "@/components/settings/project/ProjectDevicesSettings.vue";
import ProjectManagerSettingsComponent from "@/components/settings/project/ProjectManagerSettings.vue";
import DeviceManagerSettingsComponent from "@/components/settings/project/DeviceManagerSettings.vue";
import WorkingHoursSettingsComponent from "@/components/settings/timeTracking/WorkingHoursSettings.vue";
import TimeTrackingConfigurationsComponent from "@/components/settings/timeTracking/TimeTrackingConfigurations.vue";
import DailyTrackingSettingsComponent from "@/components/settings/timeTracking/DailyTrackingSettings.vue";
import SchedulingSettingsComponent from "@/components/settings/scheduling/SchedulingSettings.vue";
import UserSettingsComponent from "@/components/users/UserSettings.vue";
import BranchListComponent from "@/components/settings/company/BranchList.vue";
import ReportSettingsComponent from "@/components/settings/ReportSettings.vue";
import CompanyDataComponent from "@/components/settings/company/CompanyData.vue";
import CompanySettingsComponent from "@/components/settings/company/CompanySettings.vue";
import ReportListComponent from "@/components/report/ReportList.vue";
import NotFoundComponent from "@/components/common/NotFound.vue";
import OverviewComponent from "@/components/overview/Overview.vue";
import CockpitComponent from "@/components/overview/Cockpit.vue";
import HyDryEventMessageSettingsComponent from "@/components/settings/HyDryEventMessageSettings.vue";
import HyDryDeviceSettingsComponent from "@/components/settings/HyDryDeviceSettings.vue";
import SystemSettingsComponent from "@/components/settings/SystemSettings.vue";
import FinishedProjectsSettingsComponent from "@/components/settings/FinishedProjectsSettings.vue";
import SystemConfigurationComponent from "@/components/settings/SystemConfiguration.vue";
import RoleSettingsAdministrationComponent from "@/components/settings/administration/RoleSettingsAdministration.vue"
import UserSettingsAdministrationComponent from "@/components/settings/administration/UserSettingsAdministration.vue"
import SystemConfigurationAdministrationComponent from "@/components/settings/administration/SystemConfigurationAdministration.vue"
import AllDevicesSettingsAdministrationComponent from "@/components/settings/administration/AllDevicesSettingsAdministration.vue"
import ReportSettingsAdministrationComponent from "@/components/settings/administration/ReportSettingsAdministration.vue"
import ProjectManagementSettingsAdministrationComponent from "@/components/settings/administration/ProjectManagementSettingsAdministration.vue"
import HyDryDeviceSettingsAdministrationComponent from "@/components/settings/administration/HyDryDeviceSettingsAdministration.vue"
import CompanySettingsAdministrationComponent from "@/components/settings/administration/CompanySettingsAdministration.vue"
import HyDryMessagesSettingsAdministrationComponent from "@/components/settings/administration/HyDryMessagesSettingsAdministration.vue"
import IresConfigurationComponent from "@/components/settings/IresConfiguration.vue";
import MailboxListComponent from "@/components/settings/messages/MailboxList.vue";
import PermissionTableComponent from "@/components/users/PermissionTable.vue";

import SettingsComponent from "@/components/settings/Settings.vue";
import HelpComponent from "@/components/help/Help.vue";
import ProjectDevicesComponent from "@/components/project/devices/ProjectDevices.vue";
import ProjectDetailsComponent from "@/components/project/detail/ProjectDetails.vue";
import MessageListComponent from "@/components/messages/MessageList.vue";
import SchedulingComponent from "@/components/scheduling/Scheduling.vue";

import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { setBoolean } from "@/utils/SecureLocalStorage";
import DeviceImport from "@/components/settings/devices/import/DeviceImport.vue";
import DeviceExport from "@/components/settings/devices/export/DeviceExport.vue";
import DeviceImportHistory from "@/components/settings/devices/import/DeviceImportHistory.vue";
import DeviceCategoryList from "@/components/settings/devices/categories/DeviceCategoryList.vue";
import DeviceTemplateList from "@/components/settings/devices/categories/DeviceTemplateList.vue";
import DeviceSettings from "@/components/settings/devices/settings/DeviceSettings.vue";
import {
  ComponentName,
  hasAccess,
  hasPathAccess,
  ADMIN_PORTAL_COMPONENTS,
  MANAGE_CLIENTS_COMPONENTS,
  MANAGE_PRODUCTION_COMPONENTS,
  RATE_DRYINGS_COMPONENTS,
} from "@/utils/RouteGuards";
import { manageClientsAccess, manageProductionAccess, rateDryingsAccess } from "@/permissions/UserPermissions";
import { ensureRoleSettingsLoaded } from "@/data/repository/RoleSettingsRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { ensureSystemConfigurationLoaded } from "@/data/repository/SystemConfigurationRepository";
import { ensureProjectManagementSettingsLoaded } from "@/data/repository/SettingsRepository";
import { ProjectManagementSettingsModule } from "@/store/modules/ProjectManagementSettingsModule";
import { getProjectSettingsV2 } from "@/data/models/Settings";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";
import { resolveOfficeAdminPortalClient } from "@/data/repository/AdministrationRepository";
import { isOfficeAdminPortalClient } from "@/permissions/ClientPermissions";
import { ADMIN_COCKPIT_PATH, getCockpitPath } from "@/utils/CockpitPath";
import BillingCatalogListComponent from "@/components/settings/BillingCatalogList.vue";
import BillingCatalogDetailComponent from "@/components/settings/BillingCatalogDetail.vue";
import InfoConfigVersionListComponent from "@/components/admin/infoConfig/InfoConfigVersionList.vue";
import MiraInsurerListComponent from "@/components/admin/mira/MiraInsurerList.vue";
import MiraCompanyListComponent from "@/components/admin/mira/MiraCompanyList.vue";
import MiraExceptionListComponent from "@/components/admin/mira/MiraExceptionList.vue";
import MiraInsurerReviewComponent from "@/components/admin/mira/MiraInsurerReview.vue";
import InfoConfigVersionDetailComponent from "@/components/admin/infoConfig/InfoConfigVersionDetail.vue";
import BeschreibungDraftEditorComponent from "@/components/admin/infoConfig/BeschreibungDraftEditor.vue";
import AdminPortalComponent from "@/components/admin/AdminPortal.vue";
import MiraLandingComponent from "@/components/mira/MiraLanding.vue";
import MiraDataQualityComponent from "@/components/mira/MiraDataQuality.vue";
import MiraDryingComponent from "@/components/mira/MiraDrying.vue";
import MiraDevicesComponent from "@/components/mira/MiraDevices.vue";
import MiraBusinessDevelopmentComponent from "@/components/mira/MiraBusinessDevelopment.vue";
import MiraProcessFlowComponent from "@/components/mira/MiraProcessFlow.vue";
import MiraInsuranceComponent from "@/components/mira/MiraInsurance.vue";
import MiraUnlockComponent from "@/components/mira/MiraUnlock.vue";
import RenoReviewQueueComponent from "@/components/admin/renoReview/RenoReviewQueue.vue";
import RenoReviewStepperComponent from "@/components/admin/renoReview/RenoReviewStepper.vue";
import RenoReviewHiddenComponent from "@/components/admin/renoReview/RenoReviewHidden.vue";
import DeviceReportListComponent from "@/components/admin/deviceReports/DeviceReportList.vue";
import DeviceReportResultsComponent from "@/components/admin/deviceReports/DeviceReportResults.vue";
import DeviceReportViewerComponent from "@/components/admin/deviceReports/DeviceReportViewer.vue";
import ClientAdministrationListComponent from "@/components/admin/clients/ClientAdministrationList.vue";
import ClientAdministrationDetailComponent from "@/components/admin/clients/ClientAdministrationDetail.vue";
import ClientSetupComponent from "@/components/admin/clients/setup/ClientSetup.vue";
import ClientAdministrationDevicesComponent from "@/components/admin/clients/ClientAdministrationDevices.vue";
import ClientSensorDataExportComponent from "@/components/admin/clients/ClientSensorDataExport.vue";
import ClientAdministrationProjectsComponent from "@/components/admin/clients/ClientAdministrationProjects.vue";
import ClientAdministrationReportsComponent from "@/components/admin/clients/ClientAdministrationReports.vue";
import ClientAdministrationUsersComponent from "@/components/admin/clients/ClientAdministrationUsers.vue";
import ClientAdministrationPdfTemplatesComponent from "@/components/admin/clients/ClientAdministrationPdfTemplates.vue";
import ClientAdministrationPdfTemplateComponent from "@/components/admin/clients/ClientAdministrationPdfTemplate.vue";
import ClientPartnerDetailComponent from "@/components/admin/clients/partners/ClientPartnerDetail.vue";
import ExternalSystemListComponent from "@/components/admin/externalSystems/ExternalSystemList.vue";
import AdminCockpitComponent from "@/components/admin/cockpit/AdminCockpit.vue";
import MemberClientsManagementComponent from "@/components/administrator/MemberClientsManagement.vue";
import VehiclesComponent from "@/components/vehicles/VehiclesComponent.vue";
import VehicleDevicesListComponent from "@/components/vehicles/VehicleDevicesListComponent.vue";
import TimeTrackingHistoryComponent from "@/components/timeTracking/TimeTrackingHistory.vue";

const NAVIGATION_FAILURE_NAMES = [
  "NavigationDuplicated",
  "NavigationAborted",
  "NavigationCancelled",
  "NavigationRedirected",
];

type NavigationFailureLike = {
  name?: string;
};

type RouterNavigationResolve = (route: Route) => void;
type RouterNavigationAbort = (error: Error) => void;

function isExpectedNavigationFailure(error: unknown) {
  if (error === undefined || error === null) {
    return true;
  }

  return NAVIGATION_FAILURE_NAMES.includes(
    (error as NavigationFailureLike).name ?? ""
  );
}

const originalPush = VueRouter.prototype.push;

function patchedPush(this: VueRouter, location: RawLocation): Promise<Route>;
function patchedPush(
  this: VueRouter,
  location: RawLocation,
  onComplete?: RouterNavigationResolve,
  onAbort?: RouterNavigationAbort
): void;
function patchedPush(
  this: VueRouter,
  location: RawLocation,
  onComplete?: RouterNavigationResolve,
  onAbort?: RouterNavigationAbort
): Promise<Route> | void {
  if (onComplete || onAbort) {
    return originalPush.call(this, location, onComplete, onAbort);
  }

  return (originalPush.call(this, location) as unknown as Promise<Route>).catch(
    (error: unknown) => {
      if (isExpectedNavigationFailure(error)) {
        return this.currentRoute;
      }
      throw error;
    }
  );
}

VueRouter.prototype.push = patchedPush;

const originalReplace = VueRouter.prototype.replace;

function patchedReplace(this: VueRouter, location: RawLocation): Promise<Route>;
function patchedReplace(
  this: VueRouter,
  location: RawLocation,
  onComplete?: RouterNavigationResolve,
  onAbort?: RouterNavigationAbort
): void;
function patchedReplace(
  this: VueRouter,
  location: RawLocation,
  onComplete?: RouterNavigationResolve,
  onAbort?: RouterNavigationAbort
): Promise<Route> | void {
  if (onComplete || onAbort) {
    return originalReplace.call(this, location, onComplete, onAbort);
  }

  return (originalReplace.call(this, location) as unknown as Promise<Route>).catch(
    (error: unknown) => {
      if (isExpectedNavigationFailure(error)) {
        return this.currentRoute;
      }
      throw error;
    }
  );
}

VueRouter.prototype.replace = patchedReplace;

const COMPONENTS_FOR_SAVED_POSITION: string[] = [
  ComponentName.PROJECT_LIST,
  ComponentName.ALL_DEVICES_LIST,
  ComponentName.ALL_DEVICES,
  ComponentName.DEVICES
]

const router = new VueRouter({
  scrollBehavior(to, _, savedPosition) {
    if (savedPosition && to.name) {
      if (COMPONENTS_FOR_SAVED_POSITION.includes(to.name)) {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(savedPosition);
          }, 100);
        });
      } 
      else {
        return savedPosition;
      }
    } else {
      return { x: 0, y: 0 };
    }
  },
  routes: [
    {
      // An unknown address outside /main is sent into it, so the not-found page shows inside the
      // app with its bar and menu, and a logged-out visitor goes through the login (ticket 2610063).
      path: "*",
      redirect: "/main/notFound",
    },
    {
      path: "/",
      redirect: "/login",
    },
    {
      path: "/login/sso-failure",
      name: ComponentName.LOGIN,
      component: LoginComponent,
      meta: {
        requiresNoAuth: true,
      },
    },
    {
      path: "/login/sso-success",
      name: ComponentName.LOGIN,
      component: LoginComponent,
      meta: {
        requiresNoAuth: true,
      },
    },
    {
      path: "/login",
      name: ComponentName.LOGIN,
      component: LoginComponent,
      meta: {
        requiresNoAuth: true,
      },
    },
    {
      path: "/resetPassword",
      name: ComponentName.RESET_PASSWORD,
      component: ResetPasswordComponent,
      meta: {
        requiresNoAuth: true,
      },
    },
    {
      path: "/main",
      name: ComponentName.SCAFFOLD,
      component: Scaffold,
      children: [
        {
          path: "messages",
          name: ComponentName.ALL_MESSAGES,
          redirect: () => {
            return {
              path: "messages/messageList",
              query: {
                showAllMessages: "true",
              },
            };
          },
        },
        {
          path: ":prefix*/messageList",
          name: ComponentName.MESSAGE_LIST,
          component: MessageListComponent,
        },
        {
          path: ":prefix*/rateDrying",
          name: ComponentName.RATE_DRYING,
          component: RateDryingComponent,
        },
        {
          path: ":prefix*/dryingHistory",
          name: ComponentName.DRYING_HISTORY,
          component: DryingHistoryComponent,
          meta: {
            keepAlive: "DryingHistoryComponent",
          },
        },
        {
          path: ":prefix*/deviceDetail",
          name: ComponentName.IRES_DEVICE_DETAIL,
          component: IresDeviceDetailComponent,
          props: true,
          meta: {
            i18n: "pages.panelDetails",
          },
        },
        {
          path: ":prefix*/samples",
          name: ComponentName.SAMPLES,
          component: IresDeviceSampleListComponent,
          props: true,
        },
        {
          path: "cockpit",
          name: ComponentName.COCKPIT,
          component: CockpitComponent,
          meta: {
            keepAlive: "CockpitComponent",
            i18n: "pages.cockpit",
          },
        },
        {
          path: "help",
          name: ComponentName.HELP,
          component: HelpComponent,
          meta: {
            i18n: "pages.help",
          },
        },
        {
          path: "maps",
          name: ComponentName.MAP,
          component: MapsPageComponent,
          meta: {
            i18n: "pages.maps",
          },
        },
        {
          // IRES-only (VIEW_MIRA), refused in hasAccess() as well as hidden in the navigation.
          path: "mira",
          name: ComponentName.MIRA,
          component: MiraLandingComponent,
          meta: {
            i18n: "pages.mira",
          },
        },
        {
          path: "mira/dataQuality",
          name: ComponentName.MIRA_DATA_QUALITY,
          component: MiraDataQualityComponent,
          meta: {
            i18n: "mira.dataQuality.title",
          },
        },
        {
          path: "mira/drying",
          name: ComponentName.MIRA_DRYING,
          component: MiraDryingComponent,
          meta: {
            i18n: "mira.drying.title",
          },
        },
        {
          path: "mira/devices",
          name: ComponentName.MIRA_DEVICES,
          component: MiraDevicesComponent,
          meta: {
            i18n: "mira.devices.title",
          },
        },
        {
          path: "mira/businessDevelopment",
          name: ComponentName.MIRA_BUSINESS_DEVELOPMENT,
          component: MiraBusinessDevelopmentComponent,
          meta: {
            i18n: "mira.businessDevelopment.title",
          },
        },
        {
          path: "mira/processFlow",
          name: ComponentName.MIRA_PROCESS_FLOW,
          component: MiraProcessFlowComponent,
          meta: {
            i18n: "mira.processFlow.title",
          },
        },
        {
          path: "mira/insurance",
          name: ComponentName.MIRA_INSURANCE,
          component: MiraInsuranceComponent,
          meta: {
            i18n: "mira.insurance.title",
          },
        },
        {
          path: "mira/unlock",
          name: ComponentName.MIRA_UNLOCK,
          component: MiraUnlockComponent,
          meta: {
            i18n: "mira.unlock.title",
          },
        },
        {
          path: "scheduling",
          name: ComponentName.SCHEDULING,
          component: SchedulingComponent,
          meta: {
            i18n: "pages.scheduling",
          },
        },
        {
          path: "vehicles",
          name: ComponentName.VEHICLES,
          component: VehiclesComponent,
          meta: {
            i18n: "pages.vehicles",
          },
        },
        {
          path: "timeTrackingHistory",
          name: ComponentName.TIME_TRACKING_HISTORY,
          component: TimeTrackingHistoryComponent,
          meta: {
            i18n: "pages.timeTrackingHistory",
          },
        },
        {
          // Gated asynchronously in beforeEach via isOfficeAdminClient(); see ADMIN_PORTAL_COMPONENTS.
          path: "admin",
          name: ComponentName.ADMIN_PORTAL,
          component: AdminPortalComponent,
          meta: {
            i18n: "admin.title",
          },
        },
        {
          // Same async gate as the portal itself, plus a user-capability check inside the
          // components: portal membership alone must not imply RATE_DRYINGS (D24/D31).
          path: "admin/renoReview",
          name: ComponentName.RENO_REVIEW_QUEUE,
          component: RenoReviewQueueComponent,
          meta: {
            i18n: "admin.renoReview.title",
          },
        },
        {
          path: "admin/renoReview/step",
          name: ComponentName.RENO_REVIEW_STEPPER,
          component: RenoReviewStepperComponent,
          meta: {
            i18n: "admin.renoReview.title",
          },
        },
        {
          path: "admin/renoReview/hidden",
          name: ComponentName.RENO_REVIEW_HIDDEN,
          component: RenoReviewHiddenComponent,
          meta: {
            i18n: "admin.renoReview.hidden.title",
          },
        },
        {
          // Same async portal gate as the routes above, plus the MANAGE_PRODUCTION capability:
          // portal membership must not imply production access.
          path: "admin/deviceReports",
          name: ComponentName.DEVICE_REPORT_LIST,
          component: DeviceReportListComponent,
          meta: {
            i18n: "admin.deviceReports.title",
          },
        },
        {
          // The reports matching the filter screen's question. Filters travel in the query, so the
          // list survives a refresh and can be linked to.
          path: "admin/deviceReports/results",
          name: ComponentName.DEVICE_REPORT_RESULTS,
          component: DeviceReportResultsComponent,
          meta: {
            i18n: "admin.deviceReports.title",
          },
        },
        {
          // The report is addressed by its Storage location in the query, because an unassigned
          // report has no Firestore document to address it by.
          path: "admin/deviceReports/view",
          name: ComponentName.DEVICE_REPORT_VIEWER,
          component: DeviceReportViewerComponent,
          meta: {
            i18n: "admin.deviceReports.viewer.title",
          },
        },
        {
          // Same async portal gate, plus MANAGE_CLIENTS. The list itself is served by a callable
          // that repeats both checks server-side. Kept alive, like the other main lists, so coming
          // back from a client does not reload it; filter and search therefore stay out of the URL.
          path: "admin/clients",
          name: ComponentName.CLIENT_ADMINISTRATION_LIST,
          component: ClientAdministrationListComponent,
          meta: {
            keepAlive: "ClientAdministrationListComponent",
            i18n: "admin.clients.title",
          },
        },
        {
          // The guided setup of a new client (#2610082), from the list's +. Same gate as the list; the
          // shared IRES admin login is refused by IRES Cloud and never offered the +.
          path: "admin/clients/new",
          name: ComponentName.CLIENT_ADMINISTRATION_SETUP,
          component: ClientSetupComponent,
          meta: {
            i18n: "admin.clients.title",
          },
        },
        {
          // One client, addressed by `clientId` in the query. Same gate as the list.
          path: "admin/clients/detail",
          name: ComponentName.CLIENT_ADMINISTRATION_DETAIL,
          component: ClientAdministrationDetailComponent,
          meta: {
            i18n: "admin.clients.title",
          },
        },
        {
          // A client's devices, opened from the detail's Geräte card; a card number arrives as a
          // preset filter (`kind`, `scope`, `type`). Same gate as the list.
          path: "admin/clients/devices",
          name: ComponentName.CLIENT_ADMINISTRATION_DEVICES,
          component: ClientAdministrationDevicesComponent,
          meta: {
            i18n: "admin.clientDevices.title",
          },
        },
        {
          // A client's Sensordaten-Export (#2610088), opened from its Geräte list with that list's
          // filters. Same gate as the list.
          path: "admin/clients/devices/sensorData",
          name: ComponentName.CLIENT_ADMINISTRATION_SENSOR_DATA_EXPORT,
          component: ClientSensorDataExportComponent,
          meta: {
            i18n: "admin.clientSensorDataExport.title",
          },
        },
        {
          // A client's running and finished projects, opened from the detail's Projekte count. Kept
          // alive, so coming back from a project's reports keeps filters and position. Same gate as
          // the list.
          path: "admin/clients/projects",
          name: ComponentName.CLIENT_ADMINISTRATION_PROJECTS,
          component: ClientAdministrationProjectsComponent,
          meta: {
            keepAlive: "ClientAdministrationProjectsComponent",
            i18n: "admin.clientProjects.title",
          },
        },
        {
          // A client's reports, from the detail's Berichte count, or one project's (`projectId`)
          // from its row in the project list. Same gate as the list.
          path: "admin/clients/reports",
          name: ComponentName.CLIENT_ADMINISTRATION_REPORTS,
          component: ClientAdministrationReportsComponent,
          meta: {
            i18n: "admin.clientReports.title",
          },
        },
        {
          // A client's users, from the detail's Benutzer count. Same gate as the list.
          path: "admin/clients/users",
          name: ComponentName.CLIENT_ADMINISTRATION_USERS,
          component: ClientAdministrationUsersComponent,
          meta: {
            i18n: "admin.clientUsers.title",
          },
        },
        {
          // A client's PDF templates (`clientId`), or the shared ones (`shared=1`). Same gate.
          path: "admin/clients/pdf-templates",
          name: ComponentName.CLIENT_ADMINISTRATION_PDF_TEMPLATES,
          component: ClientAdministrationPdfTemplatesComponent,
          meta: {
            i18n: "admin.pdfTemplates.title",
          },
        },
        {
          // One template's field mapping and its PDF download (`templateId` plus the list's scope).
          path: "admin/clients/pdf-template",
          name: ComponentName.CLIENT_ADMINISTRATION_PDF_TEMPLATE,
          component: ClientAdministrationPdfTemplateComponent,
          meta: {
            i18n: "admin.pdfTemplates.title",
          },
        },
        {
          // One interface partner of a client (#2610084): `clientId` (its owner) and `partnerId`.
          path: "admin/clients/partner",
          name: ComponentName.CLIENT_ADMINISTRATION_PARTNER,
          component: ClientPartnerDetailComponent,
          meta: {
            i18n: "admin.clients.partners.title",
          },
        },
        {
          // Every external system (#2610084, merge 3), as the IRES Admin app lists them.
          path: "admin/external-systems",
          name: ComponentName.EXTERNAL_SYSTEM_LIST,
          component: ExternalSystemListComponent,
          meta: {
            i18n: "admin.clients.partners.title",
          },
        },
        {
          // Mira's insurer list: MDIS's and IRES's insurers with their spellings (Track #2610040 P3).
          path: "admin/mira-insurers",
          name: ComponentName.MIRA_INSURER_LIST,
          component: MiraInsurerListComponent,
          meta: {
            i18n: "admin.miraInsurers.title",
          },
        },
        {
          // The insurer names Mira could not resolve, grouped, to give to an insurer (Track #2610040 P3).
          path: "admin/mira-insurer-review",
          name: ComponentName.MIRA_INSURER_REVIEW,
          component: MiraInsurerReviewComponent,
          meta: {
            i18n: "admin.miraInsurerReview.title",
          },
        },
        {
          // Which Mandanten Mira's market comparison counts as one company (#2610009 C4).
          path: "admin/mira-companies",
          name: ComponentName.MIRA_COMPANY_LIST,
          component: MiraCompanyListComponent,
          meta: {
            i18n: "admin.miraCompanies.title",
          },
        },
        {
          // Mira-Ausnahmen: events a Mandant recorded late, left out of the durations (Track #2610040 R8).
          path: "admin/mira-exceptions",
          name: ComponentName.MIRA_EXCEPTION_LIST,
          component: MiraExceptionListComponent,
          meta: {
            i18n: "admin.miraExceptions.title",
          },
        },
        {
          // Every Beschreibung version (the project forms); publishing and targets live here (#56).
          path: "admin/info-config-versions",
          name: ComponentName.INFO_CONFIG_LIST,
          component: InfoConfigVersionListComponent,
          meta: {
            i18n: "admin.infoConfig.title",
          },
        },
        {
          // One version (`kind`, `version`): its rows, problems and, for a SysAdmin, target and retire.
          path: "admin/info-config-version",
          name: ComponentName.INFO_CONFIG_VERSION,
          component: InfoConfigVersionDetailComponent,
          meta: {
            i18n: "admin.infoConfig.title",
          },
        },
        {
          // The browser's draft of a new version (one at a time, kept in local storage until published).
          path: "admin/info-config-draft",
          name: ComponentName.INFO_CONFIG_DRAFT,
          component: BeschreibungDraftEditorComponent,
          meta: {
            i18n: "admin.infoConfig.title",
          },
        },
        // The German paths these screens had until 2026-10-29: routes are English (CLAUDE.md), and a bookmark
        // to the old address still lands on the screen, its query kept (a Beschreibung's kind and version).
        ...(
          [
            ["admin/mira-versicherer", ComponentName.MIRA_INSURER_LIST],
            ["admin/mira-versicherer-pruefen", ComponentName.MIRA_INSURER_REVIEW],
            ["admin/mira-firmen", ComponentName.MIRA_COMPANY_LIST],
            ["admin/mira-ausnahmen", ComponentName.MIRA_EXCEPTION_LIST],
            ["admin/beschreibungen", ComponentName.INFO_CONFIG_LIST],
            ["admin/beschreibung", ComponentName.INFO_CONFIG_VERSION],
            ["admin/beschreibung-entwurf", ComponentName.INFO_CONFIG_DRAFT],
          ] as const
        ).map(([path, name]) => ({ path, redirect: (to: Route): RawLocation => ({ name, query: to.query }) })),
        {
          // The portal client's cockpit. beforeEach sends the portal client here instead of to the
          // normal cockpit; the async portal gate applies as for every admin route.
          path: "admin/cockpit",
          name: ComponentName.ADMIN_COCKPIT,
          component: AdminCockpitComponent,
          meta: {
            i18n: "pages.cockpit",
          },
        },
        {
          path: "vehicleDevices",
          name: ComponentName.VEHICLE_DEVICES,
          component: VehicleDevicesListComponent,
          meta: {
            i18n: "pages.vehicles",
          },
        },
        {
          path: "settings",
          name: ComponentName.SETTINGS,
          component: SettingsComponent,
          meta: {
            i18n: "pages.settings",
          },
        },
        {
          path: "settings/userManagement",
          name: ComponentName.USER_MANAGEMENT,
          component: UserManagementComponent,
          meta: {
            i18n: "settings.users",
          },
        },
        {
          path: "settings/reportSettings",
          name: ComponentName.REPORT_SETTINGS,
          component: ReportSettingsComponent,
          meta: {
            i18n: "settings.report.title",
          },
        },
        {
          path: "settings/companyData",
          name: ComponentName.COMPANY_DATA,
          component: CompanyDataComponent,
          meta: {
            i18n: "settings.companySettings.companyData",
          },
        },
        {
          path: "settings/companySettings",
          name: ComponentName.COMPANY_SETTINGS,
          component: CompanySettingsComponent,
          meta: {
            i18n: "settings.companySettings.companySettingsTitle",
          },
        },
        {
          path: "settings/projectSettings",
          name: ComponentName.PROJECT_SETTINGS,
          component: ProjectSettingsComponent,
          meta: {
            i18n: "settings.projectManagement.projectSettings.title",
          },
        },
        {
          path: "settings/projectEditingSettings",
          name: ComponentName.PROJECT_EDITING_SETTINGS,
          component: ProjectEditingSettingsComponent,
          meta: {
            i18n: "settings.projectManagement.projectEditingSettings.title",
          },
        },
        {
          path: "settings/projectDevicesSettings",
          name: ComponentName.PROJECT_DEVICES_SETTINGS,
          component: ProjectDevicesSettingsComponent,
          meta: {
            i18n: "settings.allDevices.projectDevicesSettings.title",
          },
        },
        {
          path: "settings/projectManagerSettings",
          name: ComponentName.PROJECT_MANAGER_SETTINGS,
          component: ProjectManagerSettingsComponent,
          meta: {
            i18n: "settings.projectManagement.projectManagerSettings.title",
          },
        },
        {
          path: "settings/deviceManagerSettings",
          name: ComponentName.DEVICE_MANAGER_SETTINGS,
          component: DeviceManagerSettingsComponent,
          meta: {
            i18n: "settings.projectManagement.deviceManagerSettings.title",
          },
        },
        {
          path: "settings/workingHours",
          name: ComponentName.WORKING_HOURS,
          component: WorkingHoursSettingsComponent,
          meta: {
            i18n: "settings.timeTracking.workingHours",
          },
        },
        {
          path: "settings/timeTrackingConfigurations",
          name: ComponentName.TIME_TRACKING_CONFIGURATIONS,
          component: TimeTrackingConfigurationsComponent,
          meta: {
            i18n: "settings.timeTracking.configuration",
          },
        },
        {
          path: "settings/dailyTrackingSettings",
          name: ComponentName.DAILY_TRACKING_SETTINGS,
          component: DailyTrackingSettingsComponent,
          meta: {
            i18n: "settings.timeTracking.dailyTracking",
          },
        },
        {
          path: "settings/schedulingSettings",
          name: ComponentName.SCHEDULING_SETTINGS,
          component: SchedulingSettingsComponent,
          meta: {
            i18n: "settings.scheduling.planningBoard.title",
          },
        },
        {
          path: "settings/billingCatalogs",
          name: ComponentName.BILLING_CATALOG_LIST,
          component: BillingCatalogListComponent,
          meta: {
            i18n: "settings.billingCatalogs.title",
          },
        },
        {
          path: "settings/billingCatalogs/:catalogId",
          name: ComponentName.BILLING_CATALOG_DETAIL,
          component: BillingCatalogDetailComponent,
          meta: {
            i18n: "settings.billingCatalogs.title",
          },
        },
        {
          path: "settings/userSettings",
          name: ComponentName.USER_SETTINGS,
          component: UserSettingsComponent,
          meta: {
            i18n: "settings.userSettings.title",
          },
        },
        {
          path: "settings/vehicleManagement",
          name: ComponentName.VEHICLE_MANAGEMENT,
          component: VehicleManagementComponent,
          meta: {
            i18n: "settings.vehicleManagement.title",
          },
        },
        {
          path: "settings/roleManagement",
          name: ComponentName.ROLE_MANAGEMENT,
          component: RoleManagementComponent,
          meta: {
            i18n: "settings.roleManagement.title",
          },
        },
        {
          path: "settings/roleManagement/permissionTable",
          name: ComponentName.PERMISSION_TABLE,
          component: PermissionTableComponent,
          meta: {
            i18n: "settings.roleManagement.title",
          },
        },
        {
          path: "settings/branches",
          name: ComponentName.BRANCHES,
          component: BranchListComponent,
          meta: {
            i18n: "settings.branches.title",
          },
        },
        {
          path: "settings/systemConfiguration",
          name: ComponentName.SYSTEM_CONFIGURATION,
          component: SystemConfigurationComponent,
          meta: {
            i18n: "settings.system.systemConfiguration",
          },
        },
        {
          path: "settings/userSettingsAdministration",
          name: ComponentName.USER_SETTINGS_ADMINISTRATION,
          component: UserSettingsAdministrationComponent,
          meta: {
            i18n: "settings.administration.userSettings.title",
          },
        },
        {
          path: "settings/roleSettingsAdministration",
          name: ComponentName.ROLE_SETTINGS_ADMINISTRATION,
          component: RoleSettingsAdministrationComponent,
          meta: {
            i18n: "settings.administration.roleSettings.title",
          },
        },
        {
          path: "settings/systemConfigurationAdministration",
          name: ComponentName.SYSTEM_CONFIGURATION_ADMINISTRATION,
          component: SystemConfigurationAdministrationComponent,
          meta: {
            i18n: "settings.administration.systemConfiguration.title",
          },
        },
        {
          path: "settings/reportSettingsAdministration",
          name: ComponentName.REPORT_SETTINGS_ADMINISTRATION,
          component: ReportSettingsAdministrationComponent,
          meta: {
            i18n: "settings.administration.reportSettings.title",
          },
        },
        {
          path: "settings/projectManagementSettingsAdministration",
          name: ComponentName.PROJECT_MANAGEMENT_SETTINGS_ADMINISTRATION,
          component: ProjectManagementSettingsAdministrationComponent,
          meta: {
            i18n: "settings.administration.projectManagement.title",
          },
        },
        {
          path: "settings/hyDryDeviceSettingsAdministration",
          name: ComponentName.HY_DRY_DEVICE_SETTINGS_ADMINISTRATION,
          component: HyDryDeviceSettingsAdministrationComponent,
          meta: {
            i18n: "settings.administration.hyDryDeviceSettings.title",
          },
        },
        {
          path: "settings/companySettingsAdministration",
          name: ComponentName.COMPANY_SETTINGS_ADMINISTRATION,
          component: CompanySettingsAdministrationComponent,
          meta: {
            i18n: "settings.administration.companySettings.title",
          },
        },
        {
          path: "settings/iresConfiguration",
          name: ComponentName.IRES_CONFIGURATION,
          component: IresConfigurationComponent,
          meta: {
            i18n: "settings.ires.iresConfiguration",
          },
        },
        {
          path: "settings/finishedProjects",
          name: ComponentName.FINISHED_PROJECTS_SETTINGS,
          component: FinishedProjectsSettingsComponent,
          meta: {
            i18n: "settings.system.finishedProjects.title",
          },
        },
        {
          path: "settings/systemSettings",
          name: ComponentName.SYSTEM_SETTINGS,
          component: SystemSettingsComponent,
          meta: {
            i18n: "settings.system.dataStorage",
          },
        },
        {
          path: "settings/mailbox",
          name: ComponentName.MAILBOX,
          component: MailboxListComponent,
          meta: {
            i18n: "settings.mailboxes",
          },
        },
        {
          path: "settings/hyDryDeviceSettings",
          name: ComponentName.HY_DRY_DEVICE_SETTINGS,
          component: HyDryDeviceSettingsComponent,
          meta: {
            i18n: "settings.hyDryDevice.editComBoxClientConfig",
          },
        },
        {
          path: "settings/deviceImport",
          name: ComponentName.DEVICE_IMPORT,
          component: DeviceImport,
          meta: {
            i18n: "settings.hyDryDevice.devicesImport",
          },
        },
        {
          path: "settings/deviceExport",
          name: ComponentName.DEVICE_EXPORT,
          component: DeviceExport,
          meta: {
            i18n: "settings.hyDryDevice.devicesExport",
          },
        },
        {
          path: "settings/allDevicesSettingsAdministration",
          name: ComponentName.ALL_DEVICES_SETTINGS_ADMINISTRATION,
          component: AllDevicesSettingsAdministrationComponent,
          meta: {
            i18n: "settings.administration.allDevicesSettings.title",
          },
        },
        {
          path: "settings/deviceSettings",
          name: ComponentName.DEVICE_SETTINGS,
          component: DeviceSettings,
          meta: {
            i18n: "settings.hyDryDevice.configurationsImportExport",
          },
        },
        {
          path: "settings/deviceCategories",
          name: ComponentName.DEVICE_CATEGORIES,
          component: DeviceCategoryList,
          meta: {
            i18n: "settings.hyDryDevice.devicesCategorySettings",
          },
        },
        {
          path: "settings/deviceCategories/:categoryId",
          component: DeviceTemplateList,
          name: ComponentName.DEVICE_TEMPLATE_LIST,
          meta: {
            i18n: "settings.hyDryDevice.devicesCategorySettings",
          },
        },
        {
          path: "settings/deviceImportHistory",
          name: ComponentName.DEVICE_IMPORT_HISTORY,
          component: DeviceImportHistory,
          meta: {
            i18n: "settings.hyDryDevice.deviceImportHistory",
          },
        },
        {
          path: "settings/messageSelection",
          name: ComponentName.MESSAGE_SELECTION,
          component: HyDryEventMessageSettingsComponent,
          meta: {
            i18n: "settings.messageSelection",
          },
        },
        {
          path: "settings/hyDryMessagesSettingsAdministration",
          name: ComponentName.HY_DRY_MESSAGES_SETTINGS_ADMINISTRATION,
          component: HyDryMessagesSettingsAdministrationComponent,
          meta: {
            i18n: "settings.administration.hyDryMessagesSettings.title",
          },
        },
        {
          path: "overview",
          name: ComponentName.OVERVIEW,
          component: OverviewComponent,
          meta: {
            i18n: "pages.overview",
          },
        },
        {
          path: "archivedProjects",
          name: ComponentName.ARCHIVED_PROJECT_LIST,
          component: ArchivedProjectListComponent,
          meta: {
            keepAlive: "ArchivedProjectsComponent",
            i18n: "pages.archivedProjects",
          },
        },
        {
          path: "projects",
          name: ComponentName.PROJECT_LIST,
          component: ProjectListComponent,
          meta: {
            keepAlive: "ProjectsComponent",
            i18n: "pages.projects",
          },
        },
        {
          path: "cockpit/createProject",
          name: ComponentName.PROJECT_DETAILS,
          component: ProjectDetailsComponent,
          meta: {
            i18n: "pages.projects",
          },
        },
        {
          path: "createProject",
          name: ComponentName.PROJECT_DETAILS,
          component: ProjectDetailsComponent,
          meta: {
            i18n: "pages.projects",
          },
        },
        {
          path: "projects/:projectId",
          name: ComponentName.PROJECT_DETAILS,
          component: ProjectDetailsComponent,
          meta: {
            i18n: "pages.projectDetails",
          },
        },
        {
          path: "projects/:projectId/reports",
          name: ComponentName.REPORT_LIST,
          component: ReportListComponent,
        },
        {
          path: "projects/:projectId/devices/:contactPersonNumber",
          name: ComponentName.PROJECT_DEVICES,
          component: ProjectDevicesComponent,
          meta: {
            i18n: "pages.projectDetails",
          },
        },
        {
          path: "cockpit/projects/:projectId/devices/:contactPersonNumber",
          name: ComponentName.PROJECT_DEVICES,
          component: ProjectDevicesComponent,
          meta: {
            i18n: "pages.projectDetails",
          },
        },
        {
          path: "cockpit/projects/:projectId/reports",
          name: ComponentName.REPORT_LIST,
          component: ReportListComponent,
        },
        {
          path: "cockpit/projects",
          name: ComponentName.PROJECT_LIST,
          component: ProjectListComponent,
          meta: {
            keepAlive: "ProjectsComponent",
            i18n: "pages.projects",
          },
        },
        {
          path: "cockpit/projects/:projectId",
          name: ComponentName.PROJECT_DETAILS,
          component: ProjectDetailsComponent,
          meta: {
            i18n: "pages.projectDetails",
          },
        },
        {
          path: "cockpit/devices",
          name: ComponentName.IRES_DEVICE_LIST,
          component: IresDeviceList,
          meta: {
            keepAlive: "IresDeviceListComponent",
            i18n: "pages.devices",
          },
        },
        {
          path: "devices",
          name: ComponentName.IRES_DEVICE_LIST,
          component: IresDeviceList,
          meta: {
            keepAlive: "IresDeviceListComponent",
            i18n: "pages.devices",
          },
        },
        {
          path: "allDevices",
          name: ComponentName.ALL_DEVICES,
          component: AllDevices,
          meta: {
            i18n: "pages.allDevices",
          },
        },
        {
          path: "allDevices/deviceHistory",
          name: ComponentName.DEVICE_HISTORY,
          component: DeviceHistoryComponent,
          meta: {
            i18n: "pages.allDevices",
          },
        },
        {
          path: "allDevices/all",
          name: ComponentName.ALL_DEVICES_LIST,
          component: AllDevicesListComponent,
          meta: {
            i18n: "pages.allDevices",
          },
        },
        {
          path: "allDevices/:categoryId",
          name: ComponentName.DEVICE_TEMPLATE,
          component: DeviceTemplateComponent,
          meta: {
            i18n: "pages.allDevices",
          },
        },
        {
          path: "allDevices/:categoryId/:templateId/",
          name: ComponentName.DEVICES,
          component: DevicesComponent,
          meta: {
            i18n: "pages.allDevices",
          },
        },
        {
          path: "settings/memberClientsManagement",
          name: ComponentName.MEMBER_CLIENTS_MANAGEMENT,
          component: MemberClientsManagementComponent,
          meta: {
            i18n: "settings.administrator.memberClientsManagement.title",
          },
        },
        {
          // Any other address under /main (ticket 2610063). Keep it LAST: Vue Router 3 only moves a
          // top-level "*" to the end, so a nested one matches in list order and would hide every
          // route listed after it.
          path: "*",
          name: ComponentName.NOT_FOUND,
          component: NotFoundComponent,
          meta: {
            i18n: "pages.notFound",
          },
        },
      ],
    },
  ],
});

export class BackStackEntry {
  toUrl: string;
  fromUrl: string;

  keepAlive: string | undefined;

  constructor(from: Route, to: Route) {
    this.fromUrl = from.fullPath;
    this.toUrl = to.fullPath;
    this.keepAlive = from.meta?.keepAlive;
  }

  isReverseNavigation(backStackEntry: BackStackEntry) {
    return (
      backStackEntry.toUrl == this.fromUrl &&
      backStackEntry.fromUrl === this.toUrl
    );
  }
}

router.beforeEach(async (to, from, next) => {
  const safeNext = (nextArg?: Parameters<typeof next>[0]) => {
    if (nextArg === undefined) {
      next();
    } else {
      next(nextArg);
    }
  };

  // Every caller is a denied route. Say so, or the jump to the Cockpit looks like a broken link
  // (ticket 2610049). The snackbar lives in Scaffold and reads the store, so it also shows when the
  // denial happens on a page load, before Scaffold has mounted.
  const redirectToCockpitOrAbort = () => {
    SnackbarModule.showError({ key: "common.pageNotAvailable" });

    // The admin cockpit for the portal client, so a denial never bounces through the redirect below.
    const cockpitPath = getCockpitPath(isOfficeAdminPortalClient());
    const isAlreadyOnCockpit =
      from.fullPath === cockpitPath ||
      router.currentRoute.fullPath === cockpitPath;

    if (isAlreadyOnCockpit) {
      safeNext(false);
      return;
    }

    safeNext(cockpitPath);
  };

  // Check for unsaved changes before allowing navigation
  let canLeave = true;
  try {
    canLeave = await runPendingChangesGuard();
  } catch (error) {
    safeNext(false);
    return;
  }

  if (!canLeave) {
    safeNext(false);
    return;
  }

  if (
    from.name === ComponentName.SAMPLES &&
    to.name === ComponentName.IRES_DEVICE_DETAIL
  ) {
    setBoolean("KEY_OPEN_RUNS", true);
  }

  if (
    from.name === ComponentName.IRES_DEVICE_DETAIL &&
    to.name === ComponentName.PROJECT_DETAILS
  ) {
    if (from.query["startedFromDevicesTab"]) {
      setBoolean("KEY_OPEN_DEVICES_TAB", true);
    }
    if (from.query["startedFromConclusionTab"]) {
      setBoolean("KEY_OPEN_CONCLUSION_TAB", true);
    }
  }

  if (to.meta.i18n) {
    ToolbarContentModule.setI18nResource(to.meta.i18n);
  } else {
    ToolbarContentModule.setI18nResource("");
  }

  if (to.matched.some((record) => record.meta.requiresNoAuth)) {
    safeNext();
  } else {
    const unsubscribe = auth().onAuthStateChanged(async (user) => {
      unsubscribe();
      try {
        if (user) {
          const targetName = to.name as ComponentName;

          // The normal cockpit means nothing to the admin portal client, so it gets its own. One
          // redirect here covers every way in: login, the navigation, bookmarks and denied routes.
          // Reads the cached flag; if it is stale, the portal gate below resolves it, clears it and
          // falls back to the normal cockpit, which then passes this check.
          if (targetName === ComponentName.COCKPIT && isOfficeAdminPortalClient()) {
            safeNext(ADMIN_COCKPIT_PATH);
            return;
          }

          // Async admin check for the IRES admin portal. Separate allow-list from the one above:
          // adminClientIds is the Android admin app's list, officeAdminClientIds is this portal's.
          if (ADMIN_PORTAL_COMPONENTS.includes(targetName)) {
            // Resolving (rather than just reading) also refreshes the cached value the synchronous
            // isOfficeAdminPortalClient() serves to Settings and the navigation.
            const isOfficeAdmin = await resolveOfficeAdminPortalClient();
            if (!isOfficeAdmin) {
              redirectToCockpitOrAbort();
              return;
            }

            // User layer on top of the client layer. Loading role settings first is what makes a
            // page refresh work: RATE_DRYINGS is role-resolved, and the store starts on defaults
            // that grant nothing. Waiting for Scaffold's observer instead would deadlock, since
            // Scaffold is this route's own parent component.
            if (RATE_DRYINGS_COMPONENTS.includes(targetName)) {
              await ensureRoleSettingsLoaded();

              if (!rateDryingsAccess(getReactiveActiveUser())) {
                redirectToCockpitOrAbort();
                return;
              }
            }

            // Production areas, gated the same way and for the same reason.
            if (MANAGE_PRODUCTION_COMPONENTS.includes(targetName)) {
              await ensureRoleSettingsLoaded();

              if (!manageProductionAccess(getReactiveActiveUser())) {
                redirectToCockpitOrAbort();
                return;
              }
            }

            // Not gated on a capability, but its cards are: load role settings first, or a page refresh
            // shows the no-capability empty state until they arrive.
            if (targetName === ComponentName.ADMIN_COCKPIT) {
              await ensureRoleSettingsLoaded();
            }

            // Client administration, gated the same way. Cloud repeats this check on every call.
            if (MANAGE_CLIENTS_COMPONENTS.includes(targetName)) {
              await ensureRoleSettingsLoaded();

              if (!manageClientsAccess(getReactiveActiveUser())) {
                redirectToCockpitOrAbort();
                return;
              }
            }
          }

          // On a hard page load the stores still hold their defaults: Scaffold, which observes them,
          // only mounts after this guard. Read each once here, or a refresh judges the user by
          // defaults (ticket 2610049). Every call is a no-op once its store has loaded.
          await Promise.all([
            ensureRoleSettingsLoaded(),
            ensureSystemConfigurationLoaded(),
            ensureProjectManagementSettingsLoaded(),
          ]);

          const routeAccess =
            hasAccess(to.name) &&
            hasPathAccess(
              to.path,
              getProjectSettingsV2(ProjectManagementSettingsModule.projectManagementSettings)
            );

          if (!routeAccess) {
            redirectToCockpitOrAbort();
          } else {
            safeNext();
          }
        } else {
          safeNext("/login");
        }
      } catch {
        safeNext(false);
      }
    });
  }
});

export default router;
